import React from "react";
import {
  Box,
  Flex,
  HStack,
  RadioGroup,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  EnrichedSupplierForDelivery,
  SelectedDeliveryOption,
} from "../../types";
import { ValidationError } from "../../validation/deliverySelectionValidation";
import { QuickEtaOption } from "../../hooks/useManualOptionHandlers";
import { CPTSlabForManualOption } from "../../../../views/Orders/enums";
import { DeliveryOptionsTableHeader } from "./DeliveryOptionsTableHeader";
import { DeliveryOptionRow } from "./DeliveryOptionRow";
import { ManualDeliveryOptionForm } from "./ManualDeliveryOptionForm";
import { RemainingItemsBanner } from "./RemainingItemsBanner";
import { estimateSectionHeaderProps } from "../../utils/estimateSectionHeaderStyles";

export interface DeliveryOptionsPanelProps {
  supplier: EnrichedSupplierForDelivery;
  selection: SelectedDeliveryOption | undefined;
  estimateNo: string;
  isExpired: boolean;
  isRemainingItems: boolean;
  radioValue: string;
  isManualSelected: boolean;
  isDeliveryOptionSelected: (deliveryOptionId: string) => boolean;
  errorsByField: Record<string, ValidationError[] | undefined>;
  quickEtaOptions: QuickEtaOption[];
  manualSaveError: string | null;
  isSavingManualOption: boolean;
  isDeleteDisabled: boolean;
  isDeletingOption: boolean;
  onRadioChange: (value: string) => void;
  onDeliveryOptionSelect: (deliveryOptionId: string) => void;
  onManualOptionSelect: () => void;
  onOptionDelete: (optionId: string) => Promise<void>;
  onManualCPTSlabChange: (val: CPTSlabForManualOption) => void;
  onManualCPTChange: (value: string) => void;
  onManualChargeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onQuickEtaSelect: (minutes: number) => void;
  onManualEntryCancel: () => void;
  onManualEntrySave: () => void;
  isReadOnly?: boolean;
}

export const DeliveryOptionsPanel: React.FC<DeliveryOptionsPanelProps> = ({
  supplier,
  selection,
  estimateNo,
  isExpired,
  isRemainingItems,
  radioValue,
  isManualSelected,
  isDeliveryOptionSelected,
  errorsByField,
  quickEtaOptions,
  manualSaveError,
  isSavingManualOption,
  isDeleteDisabled,
  isDeletingOption,
  onRadioChange,
  onDeliveryOptionSelect,
  onManualOptionSelect,
  onOptionDelete,
  onManualCPTSlabChange,
  onManualCPTChange,
  onManualChargeChange,
  onQuickEtaSelect,
  onManualEntryCancel,
  onManualEntrySave,
  isReadOnly = false,
}) => (
  <Box flex="1.4" px={5} py={3}>
    <Flex justify="space-between" align="center" mb={2} pl={0}>
      <HStack spacing={1}>
        <Text {...estimateSectionHeaderProps}>
          DELIVERY OPTIONS
        </Text>
        <Text color="red.500" fontWeight="bold">
          *
        </Text>
      </HStack>
      {/* <Badge colorScheme="orange" variant="subtle" fontSize="0.65rem" mr={0}>
        SELECT ONE
      </Badge> */}
    </Flex>

    <RadioGroup
      value={radioValue}
      onChange={onRadioChange}
      isDisabled={isExpired || isRemainingItems || isReadOnly}
    >
      <VStack align="stretch" spacing={2}>
        <DeliveryOptionsTableHeader showDeleteColumn={!isRemainingItems && !isReadOnly} />

        {supplier.deliveryOptions.map((option) => (
          <DeliveryOptionRow
            key={option._id}
            option={option}
            isSelected={isDeliveryOptionSelected(option._id)}
            isExpired={isExpired}
            isRemainingItems={isRemainingItems}
            onSelect={() => onDeliveryOptionSelect(option._id)}
            onDelete={onOptionDelete}
            isDeleteDisabled={isDeleteDisabled}
            isDeletingOption={isDeletingOption}
            isReadOnly={isReadOnly}
          />
        ))}

        {!isReadOnly && (
          <ManualDeliveryOptionForm
            isManualSelected={isManualSelected}
            isExpired={isExpired}
            isRemainingItems={isRemainingItems}
            selection={selection}
            quickEtaOptions={quickEtaOptions}
            errorsByField={errorsByField}
            manualSaveError={manualSaveError}
            isSavingManualOption={isSavingManualOption}
            onManualOptionSelect={onManualOptionSelect}
            onManualCPTSlabChange={onManualCPTSlabChange}
            onManualCPTChange={onManualCPTChange}
            onManualChargeChange={onManualChargeChange}
            onQuickEtaSelect={onQuickEtaSelect}
            onCancel={onManualEntryCancel}
            onSave={onManualEntrySave}
          />
        )}
      </VStack>
    </RadioGroup>

    {isRemainingItems && <RemainingItemsBanner />}
  </Box>
);
